import { zodResolver } from '@hookform/resolvers/zod';
import { MailCheck } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRequestPasswordReset } from '@/hooks/use-auth';

import { AuthLayout } from './auth-layout';
import { FormError } from './form-error';
import { messageFor } from './validation-message';

const schema = z.object({
  email: z.string().min(1, 'emailRequired').pipe(z.email('emailInvalid')),
});

type Values = z.infer<typeof schema>;

export const ForgotPasswordPage = () => {
  const { t } = useTranslation('auth');
  const requestReset = useRequestPasswordReset();

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await requestReset.mutateAsync(values.email);
    } catch {
      // Shown by the mutation's error state.
    }
  });

  /**
   * The confirmation is identical whether or not the address has an account, and it
   * says "if there is an account" for exactly that reason. Anything more specific
   * would turn this screen into a way to check who is in a church's directory.
   */
  if (requestReset.isSuccess) {
    return (
      <AuthLayout title={t('forgotPassword.sentTitle')}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-body text-fg-secondary">
            <MailCheck className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
            <p>{t('forgotPassword.sentDescription', { email: form.getValues('email') })}</p>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" asChild>
              <Link to="/sign-in">{t('forgotPassword.backToSignIn')}</Link>
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('forgotPassword.title')} description={t('forgotPassword.description')}>
      <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
        <FormField
          label={t('field.email')}
          required
          error={messageFor(form.formState.errors.email?.message, t)}
        >
          {(field) => (
            <Input {...field} {...form.register('email')} type="email" autoComplete="email" autoFocus />
          )}
        </FormField>

        <FormError error={requestReset.error} />

        <div className="flex items-center justify-between gap-2">
          <Link to="/sign-in" className="text-body-sm text-brand hover:underline">
            {t('forgotPassword.backToSignIn')}
          </Link>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t('forgotPassword.submit')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};
